
import { useState, useEffect } from 'react';
import { LocationOption } from '@/types/weather';
import { useIsMobile } from '@/hooks/use-mobile';
import { toast } from '@/hooks/use-toast';

export const popularLocations: LocationOption[] = [
  { id: 'rome', name: 'Rome', country: 'Italy' },
  { id: 'milan', name: 'Milan', country: 'Italy' },
  { id: 'naples', name: 'Naples', country: 'Italy' },
  { id: 'florence', name: 'Florence', country: 'Italy' },
  { id: 'venice', name: 'Venice', country: 'Italy' },
  { id: 'turin', name: 'Turin', country: 'Italy' },
  { id: 'bologna', name: 'Bologna', country: 'Italy' },
  { id: 'palermo', name: 'Palermo', country: 'Italy' },
  { id: 'genoa', name: 'Genoa', country: 'Italy' },
  { id: 'bari', name: 'Bari', country: 'Italy' },
];

export const useLocationSearch = (searchTerm: string) => {
  const [locations, setLocations] = useState<LocationOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const isMobile = useIsMobile();

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  // Fetch location suggestions based on search term
  useEffect(() => {
    if (!debouncedSearchTerm) {
      // On mobile, limit to fewer items
      setLocations(isMobile ? popularLocations.slice(0, 2) : popularLocations);
      setIsLoading(false);
      return;
    }

    if (debouncedSearchTerm.length < 1) {
      // On mobile, limit to fewer items
      setLocations(isMobile ? popularLocations.slice(0, 2) : popularLocations);
      setIsLoading(false);
      return;
    }

    const fetchLocations = async () => {
      setIsLoading(true);
      try {
        // Add country=it to prioritize Italian locations
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(debouncedSearchTerm)}&limit=${isMobile ? 2 : 10}&addressdetails=1&countrycodes=it`,
          {
            headers: {
              'Accept-Language': 'en',
            },
          }
        );

        if (!response.ok) {
          throw new Error('Error searching for locations');
        }

        const data = await response.json();
        
        // Format the API response into our location options
        const formattedLocations: LocationOption[] = data.map((item: any) => ({
          id: `osm-${item.place_id}`,
          name: item.address?.city || item.address?.town || item.address?.village || item.display_name.split(',')[0],
          country: item.address?.country || 'Unknown',
        }));

        // If no Italian locations are found, try without country restriction
        if (formattedLocations.length === 0) {
          const globalResponse = await fetch(
            `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(debouncedSearchTerm)}&limit=${isMobile ? 2 : 10}&addressdetails=1`,
            {
              headers: {
                'Accept-Language': 'en',
              },
            }
          );
          
          if (!globalResponse.ok) {
            throw new Error('Error searching for global locations');
          }
          
          const globalData = await globalResponse.json();
          
          const globalLocations: LocationOption[] = globalData.map((item: any) => ({
            id: `osm-${item.place_id}`,
            name: item.address?.city || item.address?.town || item.address?.village || item.display_name.split(',')[0],
            country: item.address?.country || 'Unknown',
          }));
          
          setLocations(globalLocations.length > 0 ? globalLocations : (isMobile ? popularLocations.slice(0, 2) : popularLocations));
        } else {
          setLocations(formattedLocations);
        }
      } catch (error) {
        console.error(error);
        toast({
          title: "Search error",
          description: "Could not find matching locations.",
          variant: "destructive"
        });
        setLocations(isMobile ? popularLocations.slice(0, 2) : popularLocations);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLocations();
  }, [debouncedSearchTerm, isMobile]);

  return { locations, isLoading };
};
